import React from 'react';
import { Simulation } from '../../lib/supabase';

interface SimulationCardProps {
    simulation: Simulation;
    onStart: (sim: Simulation) => void;
    onSettings?: (sim: Simulation) => void;
    showSettings?: boolean;
    index?: number;
}

declare global {
    namespace JSX {
        interface IntrinsicElements {
            'model-viewer': any;
        }
    }
}

export const SimulationCard: React.FC<SimulationCardProps> = ({ simulation, onStart, onSettings, showSettings = false, index = 0 }) => {
    // Generate a determined gradient class based on index
    const gradientClass = `gradient-${(index % 5) + 1}`;

    // Get avatar URL from config
    const avatarUrl = simulation.config?.avatarUrl;

    const modelViewerRef = React.useRef<any>(null);

    React.useEffect(() => {
        const modelViewer = modelViewerRef.current;
        if (!modelViewer || !avatarUrl) return;

        let animationFrameId: number;
        let startTime = performance.now();

        const animate = () => {
            const currentTime = performance.now();
            const elapsed = currentTime - startTime;

            // Oscillate between -15deg and 15deg roughly every 8 seconds
            // Slower and gentler
            const theta = Math.sin(elapsed * 0.0005) * 15;

            // Directly update the camera-orbit attribute
            // MAINTAIN THE RADIUS HERE! It was overriding the prop.
            modelViewer.setAttribute('camera-orbit', `${theta}deg 90deg 1.35m`);

            animationFrameId = requestAnimationFrame(animate);
        };

        animate();

        return () => {
            if (animationFrameId) cancelAnimationFrame(animationFrameId);
        };
    }, [avatarUrl]);

    // Choose an icon based on title concepts or random (fallback)
    const getIcon = () => {
        const title = simulation.title.toLowerCase();
        if (title.includes('satış') || title.includes('sales')) return 'sell';
        if (title.includes('ikna') || title.includes('negotiation')) return 'handshake';
        if (title.includes('zor') || title.includes('hard')) return 'psychology_alt';
        if (title.includes('eğitim') || title.includes('training')) return 'school';
        return 'smart_toy';
    };

    return (
        <div className="simulation-card">
            <div className={`card-header-visual ${gradientClass}`}>
                {avatarUrl ? (
                    <model-viewer
                        ref={modelViewerRef}
                        src={avatarUrl}
                        alt={`Avatar for ${simulation.title}`}
                        autoplay
                        camera-controls="false"
                        disable-zoom
                        interaction-prompt="none" // Hide the hand hint
                        shadow-intensity="1"
                        style={{
                            width: '130%', // Even wider
                            height: '180%', // Even taller
                            position: 'absolute',
                            bottom: '-10%', // Push down slightly to align body
                            zIndex: 1, // Lower z-index so content can cover it
                            pointerEvents: 'none'
                        }}
                        camera-orbit="0deg 90deg 1.35m" // Intermediate distance
                        camera-target="0m 1.65m 0m" // Focus higher (on face/forehead)
                        field-of-view="30deg"
                        min-camera-orbit="auto auto 0.5m"
                        max-camera-orbit="auto auto 2m"
                    >
                    </model-viewer>
                ) : (
                    <div className="card-icon">
                        <span className="material-symbols-outlined">{getIcon()}</span>
                    </div>
                )}

                <div className="card-overlay"></div>
            </div>

            <div className="card-content">
                <h3 className="card-name">{simulation.title}</h3>
                <p className="card-description">
                    {simulation.description || 'Açıklama bulunmuyor.'}
                </p>

                <div className="card-footer">
                    {showSettings && onSettings && (
                        <button className="icon-button" onClick={() => onSettings(simulation)} title="Ayarlar">
                            <span className="material-symbols-outlined">settings</span>
                        </button>
                    )}
                    <button className="start-button-modern" onClick={() => onStart(simulation)} title="Simülasyonu Başlat">
                        <span>Başlat</span>
                        <span className="material-symbols-outlined">arrow_forward</span>
                    </button>
                </div>
            </div>
        </div>
    );
};
